import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import Loader from '../../../../../Components/Utility/Loader/Loader';
import TableBody from './TableBody';

function ProductPurchaseOrderTable({
  state,
  loading,
  error,
  totalVisibleColumns,
  isColVisible,
  handleSelectAllPO,
  handleExpandAllFalsePO,
  handleExpandAllTruePO,
  handleSelectPO,
  expandPO,
  renderGroupHeaders,
  renderGroupCells,
  isRowEditable,
  isColumnEditable,
  handleAutoSave,
  setState,
  isSupplierLoggedIn,
  POAddEditAccess,
  PODeleteAccess,
  currencySymbol,
  lengthVisible,
  handlePageNextInScroll,
  searchState,
  fetchPurchaseOrderTodos,
}) {
  const { t } = useTranslation(['purchase-order']);

  const TableContent = useMemo(() => {
    if ((state?.isInitialLoading || (state?.isTableLoading && state?.purchaseOrderTodos?.length === 0)) && loading) {
      return (
        <tr>
          <td colSpan={totalVisibleColumns} className="text-center py-4">
            <Loader />
          </td>
        </tr>
      );
    }

    if (error && state?.purchaseOrderTodos?.length === 0) {
      return (
        <tr>
          <td colSpan={totalVisibleColumns} className="text-center py-4">
            <div className="text-danger">
              <i className="icon-alert-triangle mr-2" />
              {error}
            </div>
          </td>
        </tr>
      );
    }

    if (!state?.isInitialLoading && (!state?.purchaseOrderTodos || state?.purchaseOrderTodos?.length === 0)) {
      return (
        <tr>
          <td colSpan={totalVisibleColumns} className="text-center py-3">
            <div className="font-weight-medium text-dark mb-1">{t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.NO_ORDERS_FOUND')}</div>
          </td>
        </tr>
      );
    }

    return (
      <TableBody
        state={state}
        totalVisibleColumns={totalVisibleColumns}
        isColVisible={isColVisible}
        handleSelectPO={handleSelectPO}
        expandPO={expandPO}
        renderGroupCells={renderGroupCells}
        isRowEditable={isRowEditable}
        isColumnEditable={isColumnEditable}
        handleAutoSave={handleAutoSave}
        setState={setState}
        isSupplierLoggedIn={isSupplierLoggedIn}
        POAddEditAccess={POAddEditAccess}
        PODeleteAccess={PODeleteAccess}
        currencySymbol={currencySymbol}
        lengthVisible={lengthVisible}
        fetchPurchaseOrderTodos={fetchPurchaseOrderTodos}
        t={t}
      />
    );
  }, [
    state?.isInitialLoading,
    state?.isTableLoading,
    state?.purchaseOrderTodos?.length,
    loading,
    error,
    totalVisibleColumns,
    searchState?.searchText,
    state,
    isColVisible,
    handleSelectPO,
    expandPO,
    renderGroupCells,
    isRowEditable,
    isColumnEditable,
    handleAutoSave,
    setState,
    isSupplierLoggedIn,
    POAddEditAccess,
    currencySymbol,
    lengthVisible,
    t,
  ]);

  return (
    <div className="mt-24">
      <div
        className="table-responsive mb-4"
        id="scrollableDiv_Product_Purchase_Order_Todo"
        style={{
          height: 'calc(100vh - 220px)',
          overflow: 'auto',
        }}
      >
        <InfiniteScroll
          dataLength={state?.purchaseOrderTodos?.length}
          hasMore={state.hasMore}
          next={handlePageNextInScroll}
          loader={state?.isTableLoading && <Loader />}
          scrollableTarget="scrollableDiv_Product_Purchase_Order_Todo"
          className="fixed-col-container"
        >
          <table id="POTodoTable" className="table common-custom-table mb-0">
            <thead>
              <tr>
                <th className="w-50px w-xs-40px">
                  <div className="common-checkbox margin-auto">
                    <input
                      type="checkbox"
                      onChange={handleSelectAllPO}
                      name="check_all_orders"
                      id="check_all_orders"
                      checked={state.isSelectAll}
                      disabled={!state.purchaseOrderTodos?.length}
                    />
                    <label htmlFor="check_all_orders" className="chklabel" />
                  </div>
                </th>
                <th className="w-50px w-xs-40px">
                  {state.purchaseOrderTodos?.some((o) => Array.isArray(o?.expandData) && o.expandData.length > 0) &&
                    (state.expandAll ? (
                      <button
                        type="button"
                        className="btn-up-down"
                        aria-label={t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLLAPSE_ALL')}
                        onClick={handleExpandAllFalsePO}
                      >
                        <span className="icon-new-down-arrow-icon icon-inverse" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn-up-down"
                        aria-label={t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.EXPAND_ALL')}
                        onClick={handleExpandAllTruePO}
                      >
                        <span className="icon-new-down-arrow-icon" />
                      </button>
                    ))}
                </th>
                <th className="w-120px">{t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.IMAGE')}</th>
                <th className="w-200px first-fix-col">{t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.PO_REF')}</th>
                {isColVisible('supplier') && <th className="w-150px">{t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.SUPPLIER')}</th>}
                {isColVisible('factory') && <th className="w-150px">{t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.FACTORY')}</th>}
                {isColVisible('templateName') && <th className="w-150px">{t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.TEMPLATE_NAME')}</th>}
                {isColVisible('packType') && <th className="w-120px">{t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.PACK_TYPE')}</th>}
                {isColVisible('productNumber') && <th className="w-200px">{t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.PRODUCT_NUMBER')}</th>}
                {isColVisible('brand') && <th className="w-150px">{t('COMMON_LABEL.BRAND')}</th>}
                {isColVisible('season') && <th className="w-120px">{t('COMMON_LABEL.SEASON')}</th>}
                {isColVisible('month') && <th className="w-100px">{t('COMMON_LABEL.MONTH')}</th>}
                {isColVisible('year') && <th className="w-100px">{t('COMMON_LABEL.YEAR')}</th>}
                {isColVisible('category1') && <th className="w-150px">{t('COMMON_LABEL.CATEGORY_1')}</th>}
                {isColVisible('category2') && <th className="w-150px">{t('COMMON_LABEL.CATEGORY_2')}</th>}
                {isColVisible('category3') && <th className="w-150px">{t('COMMON_LABEL.CATEGORY_3')}</th>}
                {isColVisible('category4') && <th className="w-150px">{t('COMMON_LABEL.CATEGORY_4')}</th>}
                {isColVisible('colourway') && <th className="w-150px">{t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.COLOURWAY')}</th>}
                {isColVisible('size') && <th className="w-80px">{t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.SIZE')}</th>}
                {isColVisible('length') && lengthVisible && <th className="w-120px">{t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.LENGTH')}</th>}
                {isColVisible('skuNumber') && <th className="w-120px">{t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.SKU_NUMBER')}</th>}
                {isColVisible('location') && <th className="w-120px">{t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.LOCATION')}</th>}
                {isColVisible('packRatio') && <th className="w-120px">{t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.PACK_RATIO')}</th>}
                {isColVisible('totalPacks') && <th className="w-120px">{t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.TOTAL_PACKS')}</th>}
                {isColVisible('totalUnits') &&
                  renderGroupHeaders(
                    'totalUnits',
                    {
                      original: t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.TOTAL_UNITS'),
                      actual: t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.ACTUAL_TOTAL_UNITS'),
                      revised: t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.REVISED_TOTAL_UNITS'),
                    },
                    { original: 'w-120px', actual: 'w-120px', revised: 'w-120px' },
                    { hasActual: true },
                  )}
                {isColVisible('unitPrice') && (
                  <th className="w-120px">
                    {t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.UNIT_PRICE')} ({currencySymbol})
                  </th>
                )}
                {isColVisible('discountPct') && <th className="w-120px">{t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.DISCOUNT_PCT')}</th>}
                {isColVisible('priceAfterDiscount') && (
                  <th className="w-150px">
                    {t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.PRICE_AFTER_DISCOUNT')} ({currencySymbol})
                  </th>
                )}
                {isColVisible('totalPrice') &&
                  renderGroupHeaders(
                    'totalPrice',
                    {
                      original: `${t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.TOTAL_PRICE')} (${currencySymbol})`,
                      actual: `${t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.ACTUAL_TOTAL')} (${currencySymbol})`,
                      revised: `${t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.REVISED_TOTAL')} (${currencySymbol})`,
                    },
                    { original: 'w-140px', actual: 'w-140px', revised: 'w-140px' },
                    { hasActual: true },
                  )}
                {isColVisible('approvedByDate') &&
                  renderGroupHeaders(
                    'approvedByDate',
                    {
                      original: t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.APPROVE_BY_DATE'),
                      actual: t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.ACTUAL_APPROVE_BY_DATE'),
                      revised: t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.REVISED_APPROVE_BY_DATE'),
                    },
                    { original: 'w-150px', actual: 'w-150px', revised: 'w-150px' },
                    { hasActual: true },
                  )}
                {isColVisible('exFactoryDate') &&
                  renderGroupHeaders(
                    'exFactoryDate',
                    {
                      original: t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.EX_FACTORY_DATE'),
                      actual: t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.ACTUAL_EX_FACTORY_DATE'),
                      revised: t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.REVISED_EX_FACTORY_DATE'),
                    },
                    { original: 'w-150px', actual: 'w-150px', revised: 'w-150px' },
                    { hasActual: true },
                  )}
                {isColVisible('shipmentDate') &&
                  renderGroupHeaders(
                    'shipmentDate',
                    {
                      original: t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.SHIPMENT_DATE'),
                      actual: t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.ACTUAL_SHIPMENT_DATE'),
                      revised: t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.REVISED_SHIPMENT_DATE'),
                    },
                    { original: 'w-150px', actual: 'w-150px', revised: 'w-150px' },
                    { hasActual: true },
                  )}
                {isColVisible('deliveryDate') && <th className="w-140px">{t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.DELIVERY_DATE')}</th>}
                <th className="w-120px last-fix-col">{t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLUMNS.STATUS')}</th>
              </tr>
            </thead>
            <tbody>{TableContent}</tbody>
          </table>
        </InfiniteScroll>
      </div>
    </div>
  );
}

export default ProductPurchaseOrderTable;
